import { editProjectSchema, type EditProject } from '@airpost/shared';

/**
 * The editor's unsaved changes, kept in this browser while the edit is open (ADR 085). The server holds the edit as
 * it was last saved; the page holds what the journalist does until "Αποθήκευση αλλαγών" or "Στην είδηση" saves it, or
 * "Έξοδος χωρίς αποθήκευση" drops it. The copy here only brings the work back after a closed tab or a crash: it is
 * offered when the edit opens on the same revision it was made on, and differs from it.
 */
export interface EditDraft {
  /** The revision of the edit the changes were made on. */
  base: number;
  /** When they were last kept (ISO). */
  at: string;
  project: EditProject;
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const draftKey = (editId: string): string => `ve-draft:${editId}`;

/** The browser's storage, or null where it is blocked (a private window, cleared site data). */
export function browserStore(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The kept changes worth offering when the edit opens on `revision` as `project`; null when there are none. */
export function draftToOffer(store: Store | null, editId: string, revision: number, project: EditProject): EditDraft | null {
  try {
    const raw = store?.getItem(draftKey(editId));
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<EditDraft>;
    const p = editProjectSchema.safeParse(d.project);
    if (!p.success || d.base !== revision || typeof d.at !== 'string') return null;
    if (JSON.stringify(p.data) === JSON.stringify(project)) return null;
    return { base: d.base, at: d.at, project: p.data };
  } catch {
    return null;
  }
}

export function keepDraft(store: Store | null, editId: string, draft: EditDraft): void {
  try {
    store?.setItem(draftKey(editId), JSON.stringify(draft));
  } catch {
    // a full or blocked storage: the work is still in the page
  }
}

export function dropDraft(store: Store | null, editId: string): void {
  try {
    store?.removeItem(draftKey(editId));
  } catch {
    // nothing to drop
  }
}
